<template>
  <div class="wtp-root">
    <!-- Shift Palette (Sidebar) -->
    <div class="wtp-sidebar">
      <div class="wtp-sidebar-header">
        <v-icon small color="rgba(255,255,255,0.5)">mdi-puzzle</v-icon>
        <span>Schichten</span>
      </div>
      <div class="wtp-shift-list">
        <div
          v-for="shift in availableShifts"
          :key="shift._id"
          class="wtp-shift-pill"
          :style="{ '--pill-color': shift.color }"
          draggable="true"
          @dragstart="onDragStart(shift, $event)">
          <div class="wtp-pill-dot" :style="{ background: shift.color }"></div>
          <div class="wtp-pill-info">
            <div class="wtp-pill-name">{{ shift.shortName }}</div>
            <div class="wtp-pill-full">{{ shift.name }}</div>
          </div>
          <v-icon x-small style="color: rgba(255, 255, 255, 0.2)">mdi-drag</v-icon>
        </div>
      </div>
    </div>

    <!-- Week Grid -->
    <div class="wtp-grid-area">
      <div class="wtp-grid-header">
        <span class="text-subtitle-2 font-weight-bold white--text">Wochenvorlage</span>
      </div>

      <div v-if="loading || weeklyTemplate.length !== 7" class="wtp-loading">
        <v-progress-circular indeterminate color="green accent-3" size="48" width="3"></v-progress-circular>
      </div>

      <div v-else class="wtp-week-grid">
        <div
          v-for="day in weekDays"
          :key="day.value"
          class="wtp-day-col"
          :class="{ 'wtp-weekend': day.value >= 6 }"
          @dragover.prevent="handleDragOver($event)"
          @dragleave="handleDragLeave($event)"
          @drop="onDrop(day.value, $event)">
          <!-- Day Header -->
          <div class="wtp-day-header">
            <div>
              <div class="wtp-day-name-full">{{ day.text }}</div>
              <div class="wtp-day-name-short">{{ day.short }}</div>
            </div>
            <div class="wtp-day-count" :class="{ 'has-shifts': (shiftsForDay[day.value] || []).length > 0 }">
              {{ (shiftsForDay[day.value] || []).length }}
            </div>
          </div>

          <!-- Day Summary (M/N/A) -->
          <div class="wtp-day-summary">
            <span class="wtp-mna" title="Morgen"> <span class="wtp-mna-dot morning"></span>{{ calculateDaySummaryCounts(day.value).morning }} </span>
            <span class="wtp-mna" title="Nachmittag"> <span class="wtp-mna-dot noon"></span>{{ calculateDaySummaryCounts(day.value).noon }} </span>
            <span class="wtp-mna" title="Abend"> <span class="wtp-mna-dot evening"></span>{{ calculateDaySummaryCounts(day.value).evening }} </span>
          </div>

          <!-- Shift Chips -->
          <div class="wtp-day-shifts">
            <div
              v-for="(reqShift, index) in shiftsForDay[day.value]"
              :key="`${day.value}-${reqShift.shiftTemplateId}-${index}`"
              class="wtp-shift-chip"
              :style="{ '--chip-color': reqShift.shiftTemplate ? reqShift.shiftTemplate.color : '#666' }">
              <div class="wtp-chip-left">
                <div class="wtp-chip-dot" :style="{ background: reqShift.shiftTemplate ? reqShift.shiftTemplate.color : '#666' }"></div>
                <div class="wtp-chip-info">
                  <span class="wtp-chip-name">{{ reqShift.shiftTemplate ? reqShift.shiftTemplate.shortName : "?" }}</span>
                  <span class="wtp-chip-time" v-if="reqShift.shiftTemplate">{{ getWorkTimeRange(reqShift.shiftTemplate) }}</span>
                </div>
              </div>
              <button class="wtp-chip-remove" @click.stop="removeShift(day.value, reqShift.shiftTemplateId, index)">
                <v-icon x-small color="rgba(255,255,255,0.6)">mdi-close</v-icon>
              </button>
            </div>

            <!-- Empty state -->
            <div v-if="!shiftsForDay[day.value] || shiftsForDay[day.value].length === 0" class="wtp-empty-drop">
              <v-icon x-small style="color: rgba(255, 255, 255, 0.12)">mdi-plus-circle-outline</v-icon>
              <span class="wtp-empty-text">Schicht hierher ziehen</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import Vue from "vue"

export default {
  name: "WeeklyTemplatePlanner",
  props: {
    studioId: { type: String, default: null },
    availableShifts: { type: Array, required: true },
  },
  data() {
    return {
      loading: true,
      saving: false,
      weeklyTemplate: [],
      draggedShift: null,
      weekDays: [
        { text: "Montag", short: "Mo", value: 1 },
        { text: "Dienstag", short: "Di", value: 2 },
        { text: "Mittwoch", short: "Mi", value: 3 },
        { text: "Donnerstag", short: "Do", value: 4 },
        { text: "Freitag", short: "Fr", value: 5 },
        { text: "Samstag", short: "Sa", value: 6 },
        { text: "Sonntag", short: "So", value: 7 },
      ],
    }
  },
  computed: {
    shiftsForDay() {
      const dayMap = {}
      this.weekDays.forEach((day) => {
        const dayData = this.weeklyTemplate.find((d) => d.dayOfWeek === day.value)
        dayMap[day.value] = dayData
          ? [...dayData.requiredShifts].sort((a, b) => {
              const nameA = a.shiftTemplate?.shortName || ""
              const nameB = b.shiftTemplate?.shortName || ""
              return nameA.localeCompare(nameB)
            })
          : []
      })
      return dayMap
    },
  },
  methods: {
    calculateDaySummaryCounts(dayOfWeek) {
      const dayData = this.weeklyTemplate.find((d) => d.dayOfWeek === dayOfWeek)
      if (!dayData || !dayData.requiredShifts) return { morning: 0, noon: 0, evening: 0 }
      let morningCount = 0,
        noonCount = 0,
        eveningCount = 0
      dayData.requiredShifts.forEach((reqShift) => {
        if (!reqShift.shiftTemplate || !reqShift.shiftTemplate.times || reqShift.shiftTemplate.times.length === 0) return
        let earliestStart = null,
          latestEnd = null,
          hasWorkTime = false
        reqShift.shiftTemplate.times.forEach((time) => {
          if (time.type === "Arbeitszeit" && time.start && time.end) {
            hasWorkTime = true
            const startMoment = this.$moment(time.start, "HH:mm")
            const endMoment = this.$moment(time.end, "HH:mm")
            if (endMoment.isBefore(startMoment)) endMoment.add(1, "day")
            if (!earliestStart || startMoment.isBefore(earliestStart)) earliestStart = startMoment
            if (!latestEnd || endMoment.isAfter(latestEnd)) latestEnd = endMoment
          }
        })
        if (!hasWorkTime || !earliestStart || !latestEnd) return
        const morningThreshold = this.$moment("13:00", "HH:mm")
        const noonThresholdStart = this.$moment("11:30", "HH:mm")
        const noonThresholdEnd = this.$moment("16:30", "HH:mm")
        const eveningThreshold = this.$moment("16:30", "HH:mm")
        const eveningEndThreshold = this.$moment("20:00", "HH:mm")
        if (earliestStart.isSameOrBefore(morningThreshold)) morningCount++
        if (earliestStart.isSameOrAfter(noonThresholdStart) && earliestStart.isBefore(noonThresholdEnd)) noonCount++
        if (earliestStart.isSameOrAfter(eveningThreshold) || latestEnd.isSameOrAfter(eveningEndThreshold)) eveningCount++
      })
      return { morning: morningCount, noon: noonCount, evening: eveningCount }
    },
    async loadTemplate() {
      if (!this.studioId) {
        this.weeklyTemplate = this.weekDays.map((day) => ({ dayOfWeek: day.value, requiredShifts: [] }))
        this.loading = false
        return
      }
      if (this.loading && this.weeklyTemplate.length > 0) return
      this.loading = true
      try {
        const templateDataFromApi = await api.shiftManagement.fetchWeeklyTemplate(this.studioId)
        if (!Array.isArray(templateDataFromApi)) {
          this.weeklyTemplate = this.weekDays.map((day) => ({ dayOfWeek: day.value, requiredShifts: [] }))
        } else {
          this.weeklyTemplate = this.weekDays.map((dayDef) => {
            const existingDay = templateDataFromApi.find((t) => t.dayOfWeek === dayDef.value)
            const requiredShifts = (existingDay?.requiredShifts || [])
              .map((rs) => {
                const templateId = rs.shiftTemplateId?._id || rs.shiftTemplateId
                const fullShift = this.availableShifts.find((s) => s._id === templateId)
                if (!fullShift) return null
                return { shiftTemplateId: templateId, shiftTemplate: fullShift }
              })
              .filter((rs) => rs !== null)
            return { dayOfWeek: dayDef.value, requiredShifts }
          })
        }
      } catch (error) {
        this.$toast.error("Fehler beim Laden der Vorlage.")
        this.weeklyTemplate = this.weekDays.map((day) => ({ dayOfWeek: day.value, requiredShifts: [] }))
      } finally {
        this.loading = false
      }
    },
    async saveTemplate() {
      if (!this.studioId) return
      this.saving = true
      try {
        const templateToSave = this.weeklyTemplate.map((dayData) => ({
          dayOfWeek: dayData.dayOfWeek,
          requiredShifts: dayData.requiredShifts.map((req) => ({ shiftTemplateId: req.shiftTemplateId })).filter((rs) => rs.shiftTemplateId),
        }))
        const savedTemplateData = await api.shiftManagement.saveWeeklyTemplate(this.studioId, templateToSave)
        this.$toast.success("Vorlage gespeichert!")
        this.weeklyTemplate = this.weekDays.map((dayDef) => {
          const existingDay = savedTemplateData.find((t) => t.dayOfWeek === dayDef.value)
          const requiredShifts = (existingDay?.requiredShifts || [])
            .map((rs) => {
              const templateId = rs.shiftTemplateId?._id || rs.shiftTemplateId
              const fullShift = this.availableShifts.find((s) => s._id === templateId)
              if (!fullShift) return null
              return { shiftTemplateId: templateId, shiftTemplate: fullShift }
            })
            .filter((rs) => rs !== null)
          return { dayOfWeek: dayDef.value, requiredShifts }
        })
        this.$emit("template-saved", savedTemplateData)
      } catch (error) {
        this.$toast.error("Speicherfehler.")
      } finally {
        this.saving = false
      }
    },
    onDragStart(shift, event) {
      this.draggedShift = shift
      event.dataTransfer.setData("text/plain", shift._id)
      event.dataTransfer.effectAllowed = "copy"
    },
    handleDragOver(event) {
      const col = event.currentTarget
      col.classList.add("drag-over")
    },
    handleDragLeave(event) {
      const col = event.currentTarget
      col.classList.remove("drag-over")
    },
    onDrop(targetDayOfWeek, event) {
      event.preventDefault()
      event.currentTarget.classList.remove("drag-over")
      const shiftId = event.dataTransfer.getData("text/plain")
      if (!shiftId) return
      const droppedShiftData = this.availableShifts.find((s) => s._id === shiftId)
      if (!droppedShiftData) return
      const dayDataIndex = this.weeklyTemplate.findIndex((d) => d.dayOfWeek === targetDayOfWeek)
      if (dayDataIndex > -1) {
        const newShiftEntry = { shiftTemplateId: droppedShiftData._id, shiftTemplate: { ...droppedShiftData } }
        const updatedRequiredShifts = [...this.weeklyTemplate[dayDataIndex].requiredShifts, newShiftEntry]
        Vue.set(this.weeklyTemplate[dayDataIndex], "requiredShifts", updatedRequiredShifts)
      }
      this.draggedShift = null
    },
    getWorkTimeRange(shiftTemplate) {
      if (!shiftTemplate || !shiftTemplate.times) return ""
      const workTimes = shiftTemplate.times.filter((t) => t.type === "Arbeitszeit" && t.start && t.end)
      if (workTimes.length === 0) return ""
      let earliest = null,
        latest = null
      workTimes.forEach((t) => {
        const s = this.$moment(t.start, "HH:mm")
        const e = this.$moment(t.end, "HH:mm")
        if (!earliest || s.isBefore(earliest)) earliest = s
        if (!latest || e.isAfter(latest)) latest = e
      })
      if (!earliest || !latest) return ""
      return earliest.format("HH:mm") + "–" + latest.format("HH:mm")
    },
    removeShift(dayOfWeek, shiftTemplateIdToRemove, displayIndex) {
      const dayDataIndex = this.weeklyTemplate.findIndex((d) => d.dayOfWeek === dayOfWeek)
      if (dayDataIndex > -1) {
        const sortedShifts = this.shiftsForDay[dayOfWeek]
        if (displayIndex < sortedShifts.length) {
          const shiftToRemove = sortedShifts[displayIndex]
          const actualIndex = this.weeklyTemplate[dayDataIndex].requiredShifts.findIndex((rs) => rs.shiftTemplateId === shiftToRemove.shiftTemplateId)
          if (actualIndex > -1) {
            const updatedRequiredShifts = this.weeklyTemplate[dayDataIndex].requiredShifts.filter((_, idx) => idx !== actualIndex)
            Vue.set(this.weeklyTemplate[dayDataIndex], "requiredShifts", updatedRequiredShifts)
          }
        }
      }
    },
  },
  watch: {
    studioId: {
      async handler(newId, oldId) {
        if (newId && (newId !== oldId || !this.initialLoadDone)) {
          await this.loadTemplate()
        } else if (!newId) {
          this.weeklyTemplate = this.weekDays.map((day) => ({ dayOfWeek: day.value, requiredShifts: [] }))
          this.loading = false
          this.initialLoadDone = false
        }
      },
      immediate: true,
    },
  },
}
</script>

<style scoped>
.wtp-root {
  display: flex;
  gap: 16px;
  width: 100%;
  min-height: 0;
}

/* === SIDEBAR === */
.wtp-sidebar {
  width: 180px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  overflow: hidden;
}
.wtp-sidebar-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.wtp-shift-list {
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wtp-shift-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: grab;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid transparent;
}
.wtp-shift-pill:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: var(--pill-color);
  transform: translateX(2px);
}
.wtp-shift-pill:active {
  cursor: grabbing;
  transform: scale(0.97);
}
.wtp-pill-dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}
.wtp-pill-info {
  flex: 1;
  min-width: 0;
}
.wtp-pill-name {
  font-size: 0.8rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}
.wtp-pill-full {
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.35);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* === GRID AREA === */
.wtp-grid-area {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.wtp-grid-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.wtp-save-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  letter-spacing: 0.3px !important;
}
.wtp-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
}

/* === WEEK GRID === */
.wtp-week-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  flex: 1;
}
.wtp-day-col {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  min-height: 300px;
  transition: all 0.25s ease;
}
.wtp-day-col.drag-over {
  background: rgba(76, 175, 80, 0.08) !important;
  border-color: rgba(76, 175, 80, 0.3) !important;
  box-shadow: inset 0 0 20px rgba(76, 175, 80, 0.1);
}
.wtp-weekend {
  background: rgba(255, 255, 255, 0.015);
  border-color: rgba(255, 255, 255, 0.04);
}

/* Day Header */
.wtp-day-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 6px;
}
.wtp-day-name-full {
  font-size: 0.8rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.7);
  display: block;
}
.wtp-day-name-short {
  display: none;
  font-size: 0.8rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.7);
}
@media (max-width: 1100px) {
  .wtp-day-name-full {
    display: none;
  }
  .wtp-day-name-short {
    display: block;
  }
}
.wtp-day-count {
  font-size: 0.65rem;
  font-weight: 700;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.3);
  transition: all 0.2s;
}
.wtp-day-count.has-shifts {
  background: rgba(76, 175, 80, 0.15);
  color: #66bb6a;
}

/* Day Summary */
.wtp-day-summary {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.wtp-mna {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.6rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
}
.wtp-mna-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.wtp-mna-dot.morning {
  background: #ffa726;
}
.wtp-mna-dot.noon {
  background: #42a5f5;
}
.wtp-mna-dot.evening {
  background: #ab47bc;
}

/* Shift Chips */
.wtp-day-shifts {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}
.wtp-shift-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border-left: 3px solid var(--chip-color);
  color: white;
  transition: all 0.2s ease;
  animation: chipIn 0.2s ease;
}
@keyframes chipIn {
  from {
    opacity: 0;
    transform: translateX(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
.wtp-shift-chip:hover {
  background: rgba(255, 255, 255, 0.07);
  transform: translateX(2px);
}
.wtp-chip-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.wtp-chip-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}
.wtp-chip-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.wtp-chip-name {
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wtp-chip-time {
  font-size: 0.6rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.4);
  line-height: 1.2;
  letter-spacing: 0.3px;
}
.wtp-chip-remove {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  margin-left: 4px;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
  flex-shrink: 0;
}
.wtp-shift-chip:hover .wtp-chip-remove {
  opacity: 0.5;
}
.wtp-chip-remove:hover {
  opacity: 1 !important;
}

/* Empty Drop */
.wtp-empty-drop {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px dashed rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  min-height: 40px;
  transition: all 0.2s;
}
.wtp-empty-text {
  font-size: 0.55rem;
  color: rgba(255, 255, 255, 0.1);
  text-align: center;
}
.wtp-day-col.drag-over .wtp-empty-drop {
  border-color: rgba(76, 175, 80, 0.3);
  background: rgba(76, 175, 80, 0.05);
}
.wtp-day-col.drag-over .wtp-empty-text {
  color: rgba(76, 175, 80, 0.5);
}
</style>
